import React, { useEffect, useState } from 'react';
import { DataTableColumn } from '@common/components/datatable/types';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { FormIcon } from '@modules/forms/components/form-icon';
import Icon from '@common/components/icon';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/network/selectors/network';
import moment from 'moment';
import Tooltip from '@common/components/tooltip';
import StatusBadge from '@modules/performance/components/status-badge/status-badge';
import {
  EPerformanceEvaluationStatus,
  EvaluationFormTypes,
} from '@common/definitions';
import Profile from '@common/components/profile';

export default () => {
  const { t } = useTranslation();
  const history = useHistory();
  const network = useAppSelector(selected);
  const { networkId } = useParams<{ networkId: string }>();
  const isNetworkRoute = useRouteMatch('/profile/evaluations');
  const basePath = networkId
    ? `/networks/${networkId}`
    : isNetworkRoute && network
    ? `/networks/${network}`
    : '/admin';
  const [columns, setColumns] = useState<DataTableColumn[]>([]);

  useEffect(() => {
    const baseColumns: DataTableColumn[] = [
      {
        key: 'status',
        label: t('performance:evaluation_status'),
        options: {
          size: 100,
          renderLite: (value: string) => (
            <StatusBadge status={value as EPerformanceEvaluationStatus} />
          ),
        },
      },
      {
        key: 'form',
        label: t('performance:evaluation_form'),
        options: {
          render: (row: any) => (
            <div className="tw-items-center tw-gap-3 tw-py-1 tw-w-full FormTitle">
              <FormIcon form={row.form} />
              <a
                className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter"
                onClick={() => {
                  return history.push(
                    `${basePath}/performance/evaluations/${row.id}`
                  );
                }}
                role="button"
              >
                {row.form.title}
              </a>
            </div>
          ),
        },
      },
      {
        key: 'reviewers',
        label: t('performance:evaluation_manager'),
        options: {
          renderLite: (value: any) => value.map((reviewer: any) => (
            <Profile enablePopover size={30} user={reviewer} />
          )),
        },
      },
      {
        key: 'status',
        label: t('performance:evaluation_status'),
        options: {
          render: (row: any) => {
            const selfSubmitted =
              row?.reviewee?.status === 'submitted' ||
              Boolean(row?.reviewee?.submitted_at);
            const managerSubmitted = Array.isArray(row?.reviewers)
              ? row.reviewers.some(
                  (r: any) => r?.status === 'submitted' || Boolean(r?.submitted_at)
                )
              : false;

            const isManagerType =
              row?.form?.type === EvaluationFormTypes.MANAGER;

            return (
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-[6px]">
                {!isManagerType && (
                  <div className="tw-border tw-border-solid tw-border-gray-border
                  tw-rounded-full tw-py-[2px] tw-pr-[10px] tw-pl-1"
                  >
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                      <Icon
                        type={selfSubmitted ? 'circle_check' : 'circle'}
                        size={14}
                        color={selfSubmitted ? 'green-success' : 'grey-dull'}
                      />
                      <div className="tw-font-medium tw-text-gray-text tw-font-sfpro">
                        {t('performance:self')}
                      </div>
                    </div>
                  </div>
                )}
                <div className="tw-border tw-border-solid tw-border-gray-border tw-rounded-full tw-py-[2px] tw-pr-[10px] tw-pl-1">
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                    <Icon
                      type={managerSubmitted ? 'circle_check' : 'circle'}
                      size={14}
                      color={managerSubmitted ? 'green-success' : 'grey-dull'}
                    />
                    <div className="tw-font-medium tw-text-gray-text tw-font-sfpro">
                      {t('performance:manager')}
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
      },
      {
        key: 'created_at',
        label: t('performance:started_at'),
        options: {
          size: 120,
          render: (row: any) => (
            <Tooltip
              fullTitle
              title={moment(row.created_at).format('MMMM D, YYYY HH:mm:ss')}
              placement="bottom"
            >
              <div>{moment(row.created_at).format('MMM D, YYYY')}</div>
            </Tooltip>
          ),
        },
      },
      {
        key: 'completed_at',
        label: t('performance:completed_at'),
        options: {
          size: 150,
          render: (row: any) => (
            <Tooltip
              fullTitle
              disabled={!row.completed_at}
              title={moment(row.completed_at).format('MMMM D, YYYY HH:mm:ss')}
              placement="bottom"
            >
              <div>
                {row.completed_at
                  ? moment(row.completed_at).format('MMM D, YYYY')
                  : '-'}
              </div>
            </Tooltip>
          ),
        },
      },
    ];

    setColumns(baseColumns);
  }, [t, basePath]);

  return columns;
};
